import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  DashboardStats, 
  VOD,
  P2PUser,
  ApiResponse,
  PaginatedResponse,
  AuthResponse,
  LoginCredentials,
  UserFilter
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private uploadApi: AxiosInstance; // Client separado para uploads
  private cancelTokenSource: any = null; // Para cancelar requisiÃƒÂ§ÃƒÂµes pendentes

  constructor() {
    // Conectar com nossa API Node.js
    // Usar REACT_APP_API_URL para ambos ambientes, com fallbacks
    const baseURL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001'
        : 'https://api-cms.cms20.online');
    
    // URL direta para uploads (bypass Cloudflare)
    const directURL = process.env.REACT_APP_API_DIRECT_URL || baseURL;

    this.api = axios.create({
      baseURL,
      timeout: 10000, // 10 segundos para requisiÃƒÂ§ÃƒÂµes normais
      headers: {
        // NÃƒÂ£o defina Content-Type globalmente para nÃƒÂ£o conflitar com multipart/form-data
        'Accept': 'application/json',
      },
      withCredentials: true,
    });

    // Client separado para uploads grandes (sem timeout, vai direto para API)
    this.uploadApi = axios.create({
      baseURL: directURL,
      timeout: 0, // Sem timeout para uploads grandes
      headers: {
        'Accept': 'application/json',
      },
      withCredentials: true,
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    });

    // Garantir que nÃƒÂ£o haja Content-Type default que interfira em FormData
    try {
      delete (this.api.defaults.headers as any).common['Content-Type'];
      delete (this.api.defaults.headers as any).post['Content-Type'];
      delete (this.api.defaults.headers as any).put['Content-Type'];
      delete (this.api.defaults.headers as any).patch['Content-Type'];
      
      // Mesmo para uploadApi
      delete (this.uploadApi.defaults.headers as any).common['Content-Type'];
      delete (this.uploadApi.defaults.headers as any).post['Content-Type'];
      delete (this.uploadApi.defaults.headers as any).put['Content-Type'];
      delete (this.uploadApi.defaults.headers as any).patch['Content-Type'];
    } catch (_) {}

    // Request interceptor para adicionar auth token (para ambos os clients)
    const requestInterceptor = (config: any) => {
      // Garantir que headers existe
      if (!config.headers) {
        config.headers = {};
      }
      
      // Incluir o slug atual do CMS em todas as requisiÃƒÂ§ÃƒÂµes
      try {
        const path = window.location.pathname || '';
        const slug = path.split('/')[1] || '';
        if (slug) {
          config.headers['X-CMS-Slug'] = slug;
        }
      } catch (_) {}
      
      // Tentar recuperar token de mÃƒÂºltiplas fontes (failsafe para diferentes navegadores)
      // PRIORIDADE: _pv001 (formato usado pelo sistema) > auth-storage > token direto
      let token: string | null = null;
      
      try {
        // 1. Verificar localStorage disponÃƒÂ­vel
        if (typeof localStorage === 'undefined') {
          return config;
        }
        
        // 2. PRIORIDADE 1: Tentar _pv001 (formato usado pelo sistema)
        try {
          const storage = localStorage.getItem('_pv001');
          if (storage) {
            const obj = JSON.parse(storage);
            // Verificar mÃƒÂºltiplas possÃƒÂ­veis chaves para o token
            token = obj.t || obj.token || obj.accessToken || obj.authToken || null;
            
            // Validar token antes de usar
            if (token) {
              // Garantir que o token ÃƒÂ© uma string vÃƒÂ¡lida e nÃƒÂ£o vazia
              const tokenStr = String(token).trim();
              if (tokenStr && tokenStr.length > 0) {
                // Verificar formato bÃƒÂ¡sico de JWT (3 partes separadas por ponto)
                const tokenParts = tokenStr.split('.');
                if (tokenParts.length === 3) {
                  config.headers.Authorization = `Bearer ${tokenStr}`;
                  
                  return config;
                }
              }
            }
          }
        } catch (legacyError) {
          // Erro silenciado para evitar vazamento de dados
        }
        
        // 3. PRIORIDADE 2: Tentar auth-storage (Zustand - formato novo)
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const authData = JSON.parse(authStorage);
            token = authData.state?.token || authData.token || authData.state?.accessToken || null;
            if (token) {
              const tokenStr = String(token).trim();
              if (tokenStr && tokenStr.length > 0) {
                // Verificar formato bÃƒÂ¡sico de JWT
                const tokenParts = tokenStr.split('.');
                if (tokenParts.length === 3) {
                  config.headers.Authorization = `Bearer ${tokenStr}`;
                  return config;
                }
              }
            }
          }
        } catch (zustandError) {
          // Silenciar erro, continuar tentando outras fontes
        }
        
        // 4. PRIORIDADE 3: Tentar token direto no localStorage (fallback)
        try {
          const directToken = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
          if (directToken) {
            const tokenStr = String(directToken).trim();
            if (tokenStr && tokenStr.length > 0) {
              // Verificar formato bÃƒÂ¡sico de JWT
              const tokenParts = tokenStr.split('.');
              if (tokenParts.length === 3) {
                token = tokenStr;
                config.headers.Authorization = `Bearer ${tokenStr}`;
                return config;
              }
            }
          }
        } catch (directError) {
          // Silenciar erro
        }
        
      } catch (error) {
        // Erro silenciado para evitar vazamento de dados
      }
      
      // Se nÃƒÂ£o encontrou token, fazer uma ÃƒÂºltima tentativa mais abrangente
      if (!token) {
        // Tentar buscar de todas as possÃƒÂ­veis fontes novamente com mais cuidado
        // PRIORIDADE: _pv001 primeiro (formato usado pelo sistema)
        const allPossibleTokens = [
          () => {
            try {
              const legacy = localStorage.getItem('_pv001');
              if (legacy) {
                const parsed = JSON.parse(legacy);
                return parsed.t || parsed.token || parsed.accessToken || parsed.authToken || null;
              }
            } catch {}
            return null;
          },
          () => {
            try {
              const authStorage = localStorage.getItem('auth-storage');
              if (authStorage) {
                const parsed = JSON.parse(authStorage);
                return parsed.state?.token || parsed.token || parsed.state?.accessToken || null;
              }
            } catch {}
            return null;
          },
          () => localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || null
        ];
        
        for (const getToken of allPossibleTokens) {
          try {
            const foundToken = getToken();
            if (foundToken) {
              const tokenStr = String(foundToken).trim();
              if (tokenStr && tokenStr.length > 0) {
                // Verificar formato bÃƒÂ¡sico de JWT
                const tokenParts = tokenStr.split('.');
                if (tokenParts.length === 3) {
                  token = tokenStr;
                  config.headers.Authorization = `Bearer ${tokenStr}`;
                  
                  return config;
                }
              }
            }
          } catch (err) {
            // Continuar tentando
          }
        }
        
      }
      
      // Garantir que retornamos config mesmo sem token
      return config;
    };

    // Aplicar interceptor em ambos os clients
    this.api.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
    this.uploadApi.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));

    // Response interceptor to handle auth errors and HTML responses
    this.api.interceptors.response.use(
      (response) => {
        // Verificar se a resposta ÃƒÂ© HTML quando esperamos JSON
        const contentType = response.headers['content-type'];
        const acceptHeader = response.config.headers?.Accept;
        if (typeof contentType === 'string' && contentType.includes('text/html') && 
            typeof acceptHeader === 'string' && acceptHeader.includes('application/json')) {
          throw new Error('API retornou HTML em vez de JSON. Verifique se a rota da API estÃƒÂ¡ funcionando corretamente.');
        }
        return response;
      },
      async (error) => {
        // Tratar casos onde a API retorna HTML em vez de JSON
        if (error.response?.data && typeof error.response.data === 'string' && 
            (error.response.data.includes('<!doctype') || error.response.data.includes('<html'))) {
          const apiError = new Error('API retornou uma pÃƒÂ¡gina HTML em vez de JSON. Verifique a configuraÃƒÂ§ÃƒÂ£o da API.');
          return Promise.reject(apiError);
        }
        
        // Se receber 401 (Unauthorized), fazer logout EXCETO se for na rota de login
        // Na rota de login, 401 significa senha incorreta, nÃƒÂ£o token expirado
        // NÃƒÂ£o redirecionar - deixar o componente tratar o erro
        const isLoginRoute = error.config?.url?.includes('/auth/login');
        const isMigrateRoute = error.config?.url?.includes('/auth/migrate-xui');
        
        if (error.response?.status === 401 && !isLoginRoute && !isMigrateRoute) {
          // Verificar se ÃƒÂ© realmente um erro de token ou outro tipo de 401
          const errorMessage = error.response?.data?.error || '';
          const isTokenError = errorMessage.toLowerCase().includes('token') || 
                              errorMessage.toLowerCase().includes('expirado') ||
                              errorMessage.toLowerCase().includes('invÃƒÂ¡lido') ||
                              errorMessage.toLowerCase().includes('nÃƒÂ£o fornecido') ||
                              errorMessage.toLowerCase().includes('unauthorized');
          
          // Apenas limpar localStorage se for realmente um erro de token
          if (isTokenError) {
            // Token expirado ou invÃƒÂ¡lido - cancelar todas as requisiÃƒÂ§ÃƒÂµes pendentes
            // e limpar localStorage
            this.cancelAllPendingRequests();
            localStorage.clear();
            
            // Redirecionar para login apenas se estiver em uma rota protegida
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
              window.location.href = '/login';
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints (conectando com nossa API Node.js)
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response: AxiosResponse<any> = await this.api.post('/api/auth/login', credentials);
    
    if (response.data && response.data.token) {
      const tokenValue = String(response.data.token).trim();
      
      // Validar token antes de salvar
      if (!tokenValue || tokenValue.length === 0) {
        throw new Error('Token recebido do servidor estÃƒÂ¡ vazio ou invÃƒÂ¡lido');
      }
      
      // Verificar formato bÃƒÂ¡sico de JWT (3 partes separadas por ponto)
      const tokenParts = tokenValue.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Token recebido do servidor nÃƒÂ£o tem formato JWT vÃƒÂ¡lido');
      }
      
      // Salvar token no mesmo formato do sistema antigo
      const obj = {"t": tokenValue, "l": true};
      localStorage.setItem("_pv001", JSON.stringify(obj));
      
      // Verificar se foi salvo corretamente
      const saved = localStorage.getItem('_pv001');
      if (!saved) {
        throw new Error('Falha ao salvar token no localStorage');
      }
      
      // Verificar se o token salvo pode ser recuperado
      try {
        const parsed = JSON.parse(saved);
        const savedToken = parsed.t || parsed.token || null;
        if (!savedToken || String(savedToken).trim() !== tokenValue) {
          throw new Error('Token salvo nÃƒÂ£o corresponde ao token recebido');
        }
      } catch (verifyError) {
        throw new Error('Falha ao verificar token salvo');
      }
      
      return {
        user: response.data.user,
        token: tokenValue,
      };
    }
    
    throw new Error(response.data.error || 'Erro no login');
  }

  async logout(): Promise<void> {
    try {
      // Cancelar todas as requisiÃƒÂ§ÃƒÂµes pendentes antes de fazer logout
      this.cancelAllPendingRequests();
      await this.api.post('/api/auth/logout');
    } catch (error) {
      // Ignorar erros de logout (pode ser que o token jÃƒÂ¡ esteja expirado)
    } finally {
      localStorage.clear();
    }
  }

  // MÃƒÂ©todo para cancelar todas as requisiÃƒÂ§ÃƒÂµes pendentes
  private cancelAllPendingRequests(): void {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Logout realizado - cancelando requisiÃƒÂ§ÃƒÂµes pendentes');
      this.cancelTokenSource = null;
    }
  }


  // MigraÃƒÂ§ÃƒÂ£o XUI - Primeiro login
  async migrateXuiUser(credentials: {
    username: string;
    email: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<AuthResponse> {
    const response: AxiosResponse<any> = await this.api.post('/api/auth/migrate-xui', credentials);
    
    if (response.data && response.data.token) {
      // Salvar token no mesmo formato do sistema antigo
      const obj = {"t": `${response.data.token}`, "l": true};
      localStorage.setItem("_pv001", JSON.stringify(obj));
      
      return {
        user: response.data.user,
        token: response.data.token,
      };
    }
    
    throw new Error(response.data.error || 'Erro na migraÃƒÂ§ÃƒÂ£o');
  }

  // Profile endpoints
  async getProfile(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/profile');
    return response.data.data; // Retorna apenas os dados do usuÃƒÂ¡rio
  }

  async updateProfile(profileData: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/api/profile', profileData);
    return response.data;
  }

  async changePassword(passwordData: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/profile/password', passwordData);
    return response.data;
  }

  async updateNotificationSettings(notifications: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/profile/notifications', { notifications });
    return response.data;
  }

  async updatePreferences(preferences: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/profile/preferences', { preferences });
    return response.data;
  }

  async getActivityHistory(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      limit: options.limit || 20,
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/profile/activity', { params });
    return response.data;
  }

  async updateAvatar(avatarUrl: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/profile/avatar', { avatarUrl });
    return response.data;
  }

  async removeAvatar(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete('/profile/avatar');
    return response.data;
  }

  async getProfileStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/profile/stats');
    return response.data;
  }

  // Credits endpoints
  async getCredits(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/credits');
    return response.data;
  }

  async updateCredits(update: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/api/credits', update);
    return response.data;
  }

  async deductCredits(deduction: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/credits/deduct', deduction);
    return response.data;
  }

  async getCreditsHistory(page: number = 1, limit: number = 20): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/credits/history', {
      params: { page, limit }
    });
    return response.data;
  }

  // Logs endpoints
  async getLogs(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 15,
      category: options.category || '',
      subcategory: options.subcategory || '',
      level: options.level || '',
      search: options.search || '',
      search_mode: options.search_mode || 'all',
      date_from: options.date_from || '',
      date_to: options.date_to || '',
      user_id: options.user_id || '',
      target_id: options.target_id || '',
      target_type: options.target_type || '',
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/logs', { params });
    return response.data;
  }

  async getLogsStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/logs/stats');
    return response.data;
  }

  async getLogsCategories(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/logs/categories');
    return response.data;
  }

  /** Admin: limpar logs. confirm deve ser "LIMPAR". */
  async clearLogs(options: { confirm: string; older_than?: string; category?: string } = { confirm: 'LIMPAR' }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete('/api/logs/clear', {
      data: {
        confirm: options.confirm,
        older_than: options.older_than || undefined,
        category: options.category || undefined
      }
    });
    return response.data;
  }

  // Dashboard endpoints (baseado no sistema antigo)
  async getDashboardStats(): Promise<DashboardStats> {
    // Timeout aumentado para 30s devido ÃƒÂ s queries complexas do dashboard
    const response: AxiosResponse<any> = await this.api.get('/api/dashboard', {
      timeout: 30000 // 30 segundos para queries complexas
    });
    return response.data;
  }

  async getRevenueHistory(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/dashboard/revenue-history', {
      timeout: 30000 // 30 segundos para queries complexas
    });
    return response.data;
  }

  async getTopResellersActivations(options?: { period?: string; mode?: 'all' | 'active_only'; include_admins?: boolean }): Promise<{ success: boolean; data: Array<{ id: number; username: string; activationsCount: number }>; period: { month: number; year: number }; filters?: { mode: string; include_admins: boolean } }> {
    const params = new URLSearchParams();
    if (options?.period) params.append('period', options.period);
    if (options?.mode) params.append('mode', options.mode);
    if (options?.include_admins !== undefined) params.append('include_admins', String(options.include_admins));
    const query = params.toString();
    const url = query ? `/api/dashboard/top-resellers-activations?${query}` : '/api/dashboard/top-resellers-activations';
    const response: AxiosResponse<any> = await this.api.get(url, { timeout: 15000 });
    return response.data;
  }

  async getDashboardNews(): Promise<any[]> {
    const response: AxiosResponse<any> = await this.api.get('/news');
    return response.data;
  }

  async updateNews(id: string, newsItem: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/news/${id}`, newsItem);
    return response.data;
  }

  async getUser(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${id}`);
    return response.data;
  }

  async createTrialUser(user: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/trial_users', user);
    return response.data;
  }

  async updateUser(id: string, user: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/lines/${id}`, user);
    return response.data;
  }

  async deleteUser(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/lines/${id}`);
    return response.data;
  }

  // User Management Functions (baseado no CMS antigo)
  async renewUser(lineId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${lineId}/renew`, params);
    return response.data;
  }


  async blockUser(userId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${userId}/block`);
    return response.data;
  }

  async unblockUser(userId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${userId}/unblock`);
    return response.data;
  }

  async changeUserSystem(lineId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${lineId}/change_system`, params);
    return response.data;
  }

  async changeUserPackage(lineId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${lineId}/change_package`, params);
    return response.data;
  }

  async extendUser(lineId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${lineId}/extend`, params);
    return response.data;
  }


  async getLineUserInfo(lineId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${lineId}/user-info`);
    return response.data;
  }

  // Nova funÃƒÂ§ÃƒÂ£o para pacotes da API de linhas (para alteraÃƒÂ§ÃƒÂ£o de plano)
  async getLinePackages(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/packages');
    return response.data;
  }

  async resyncUserP2p(userId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${userId}/resync-p2p`, {});
    return response.data;
  }

  async resyncAllP2pUsers(params: { includeMissing?: boolean; delayMs?: number } = {}): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/lines/resync-p2p-all', params);
    return response.data;
  }

  async getResyncAllP2pStatus(jobId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/resync-p2p-all/${jobId}`);
    return response.data;
  }

  async updateUserScreens(userId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${userId}/screens`, params);
    return response.data;
  }

  async registerLazyPlayer(userId: string, expDate: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/lines/${userId}/activesmart`, {
      user_id: userId,
      exp_date: expDate
    });
    return response.data;
  }

  async migrateUser(params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/lines/migrate', params);
    return response.data;
  }

  async fetchUrlData(url: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/lines/fetch-url-data', url);
    return response.data;
  }

  async getExpiringUsers(options: any = {}): Promise<any> {
    const queryParams = new URLSearchParams(options).toString();
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/expiring?${queryParams}`);
    return response.data;
  }

  async disconnectUser(userId: string, activeId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/connections/${userId}/${activeId}/disconect`);
    return response.data;
  }

  async userSelectSearch(text: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/lines/select_search', { search: text });
    return response.data;
  }

  // Groups endpoints
  async getGroups(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 100,
      order_by: options.order_by || 'id',
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/groups', { params });
    return response.data;
  }

  async getUserConnections(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 10,
      order_by: options.order_by || '',
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/users/connections', { params });
    return response.data;
  }


  // ===========================================
  // RESELLERS ENDPOINTS (Revendedores Xtream - MySQL)
  // ===========================================
  // Revendedores sÃƒÂ£o os usuÃƒÂ¡rios que revendem o serviÃƒÂ§o IPTV
  // Eles existem no banco MySQL do Xtream
  
  async getResellers(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 15,
      order_by: options.order_by || 'created_at',
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/resellers', { params });
    return response.data;
  }

  async getResellersStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/resellers/stats');
    return response.data;
  }

  // ===========================================
  // CMS USERS ENDPOINTS (UsuÃƒÂ¡rios do CMS - MongoDB)
  // ===========================================
  // UsuÃƒÂ¡rios CMS sÃƒÂ£o contas que tÃƒÂªm acesso ao painel de administraÃƒÂ§ÃƒÂ£o
  // Eles existem no banco MongoDB e sÃƒÂ£o vinculados aos revendedores do MySQL
  
  async getCmsUsers(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 15,
      search: options.search || undefined,
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/users/cms', { params });
    return response.data;
  }

  async getCmsUser(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/users/cms/${id}`);
    return response.data.user || response.data;
  }

  async updateCmsUser(id: string, payload: Partial<{ username: string; name: string; email: string; role: string; settings: any; colors: any; permissions: any }>): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/users/cms/${id}`, payload);
    return response.data.user || response.data;
  }

  async getCmsUsersStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/users/cms/stats');
    return response.data;
  }

  // Buscar usuÃƒÂ¡rio CMS pelo MySQL ID (para verificar se jÃƒÂ¡ existe)
  async getCmsUserByMysqlId(mysqlUserId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/users/${mysqlUserId}/mongo`);
    return response.data;
  }

  // Sincronizar revendedor do Xtream para CMS
  async syncUserToCms(userId: number, cmsPassword: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/users/sync-to-cms', {
      userId,
      cmsPassword
    });
    return response.data;
  }

  async changeCmsPassword(mysqlUserId: number, password: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/users/cms/mysql/${mysqlUserId}/password`, {
      password
    });
    return response.data;
  }

  async updateCmsUserStatus(userId: string, status: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/users/cms/${userId}/status`, { status });
    return response.data;
  }

  async generateCmsUserApiKey(userId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/users/cms/${userId}/api-key`);
    return response.data;
  }

  async deleteCmsUser(userId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/users/cms/${userId}`);
    return response.data;
  }

  async updateCmsUserPermissions(userId: string, permissions: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/users/cms/${userId}`, { permissions });
    return response.data;
  }

  // CMS User Settings
  async getCmsUserCurrent(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/users/cms/current');
    return response.data;
  }

  async updateCmsUserProfile(profileData: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/api/users/cms/settings', profileData);
    return response.data;
  }


  // ===========================================
  // LINES ENDPOINTS (Clientes Finais - MySQL)
  // ===========================================
  // Lines sÃƒÂ£o os clientes finais que usam o serviÃƒÂ§o IPTV
  // Cada line representa uma conta de cliente com suas credenciais e plano
  
  async getLines(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 10,
      order_by: options.order_by || '',
      ...options
    };
    // Timeout maior - listagem de linhas pode ser lenta com muitos dados
    const response: AxiosResponse<any> = await this.api.get('/api/lines', {
      params,
      timeout: 30000 // 30 segundos para queries de listagem
    });
    return response.data;
  }

  async getLine(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${id}`);
    return response.data;
  }


  async updateLine(id: string, line: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/lines/${id}`, line);
    return response.data;
  }

  async deleteLine(id: string, options?: { bulkQueue?: boolean }): Promise<any> {
    const headers =
      options?.bulkQueue === true ? { 'X-Bulk-Delete-Queue': '1' } : undefined;
    const response: AxiosResponse<any> = await this.api.delete(`/api/lines/${id}`, {
      headers
    });
    return response.data;
  }

  // ==================== BULK DELETE LINES ====================

  async getBulkDeleteLinesPreview(
    filterType:
      | 'expired'
      | 'expired_trial'
      | 'expired_non_trial'
      | 'expired_days'
      | 'expired_days_trial'
      | 'trial',
    daysExpired: number = 0,
    onlyTrial: boolean = false
  ): Promise<any> {
    try {
      const params = new URLSearchParams({
        filterType,
        daysExpired: daysExpired.toString(),
        onlyTrial: onlyTrial.toString()
      });
      const response: AxiosResponse = await this.api.get(
        `/api/lines/bulk-delete/preview?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async bulkDeleteLines(
    filterType:
      | 'expired'
      | 'expired_trial'
      | 'expired_non_trial'
      | 'expired_days'
      | 'expired_days_trial'
      | 'trial',
    daysExpired: number = 0,
    onlyTrial: boolean = false,
    batchSize: number = 50
  ): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post(
        '/api/lines/bulk-delete',
        {
          filterType,
          daysExpired,
          onlyTrial,
          confirm: true,
          batchSize
        },
        { timeout: 180000 }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /** IDs que batem com o filtro (mesmo critÃƒÂ©rio do preview) Ã¢â‚¬â€ para fila no cliente: deleteLine(id) um a um. */
  async getBulkDeleteLineIds(
    filterType:
      | 'expired'
      | 'expired_trial'
      | 'expired_non_trial'
      | 'expired_days'
      | 'expired_days_trial'
      | 'trial',
    daysExpired: number = 0,
    onlyTrial: boolean = false
  ): Promise<{
    success: boolean;
    data?: {
      ids: number[];
      returned: number;
      totalMatching: number;
      truncated: boolean;
    };
    error?: string;
  }> {
    const params = new URLSearchParams({
      filterType,
      daysExpired: daysExpired.toString(),
      onlyTrial: onlyTrial.toString()
    });
    const response: AxiosResponse = await this.api.get(
      `/api/lines/bulk-delete/ids?${params.toString()}`
    );
    return response.data;
  }



  async getLineSelectSearch(text: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/lines/select_search', { search: text });
    return response.data.data;
  }

  async getLinesExpiring(options: any = {}): Promise<any> {
    const params = {
      page: options.page || 1,
      per: options.per || 10,
      order: options.order || 'asc',
      ...options
    };
    
    const response: AxiosResponse<any> = await this.api.get('/api/lines/expiring', { params });
    return response.data;
  }

  async blockLine(lineId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${lineId}/block`);
    return response.data;
  }

  async unblockLine(lineId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/lines/${lineId}/unblock`);
    return response.data;
  }

  async getLinesStats(options: any = {}): Promise<any> {
    // EstatÃƒÂ­sticas podem levar mais tempo, usar timeout maior
    const response: AxiosResponse<any> = await this.api.get('/api/lines/stats', {
      params: {
        owner_id: options.owner_id || undefined
      },
      timeout: 30000 // 30 segundos para estatÃƒÂ­sticas
    });
    return response.data;
  }

  // Dashboard endpoints
  async getExpiringClients(includeTrials: boolean = false, page: number = 1, per: number = 10): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (includeTrials) {
        params.append('include_trials', 'true');
      }
      params.append('page', page.toString());
      params.append('per', per.toString());
      
      const url = `/api/dashboard/expiring-clients?${params.toString()}`;
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async quickRenewClient(lineId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/lines/quick-renew', {
        line_id: lineId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Adicionar telas (conexÃƒÂµes) ÃƒÂ  linha
  async addScreensToLine(lineId: string, screensToAdd: number): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/lines/add-screens', {
        line_id: lineId,
        screens_to_add: screensToAdd
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  async trackAppDownload(appId: string, platform: string, userAgent: string, ipAddress: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/dashboard/app-downloads/track', {
        app_id: appId,
        platform,
        user_agent: userAgent,
        ip_address: ipAddress
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Announcements endpoints
  async getAnnouncements(limit?: number): Promise<any> {
    try {
      const params = limit ? { limit } : {};
      const response: AxiosResponse<any> = await this.api.get('/api/announcements', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAnnouncementsAdmin(params?: { page?: number; per?: number; search?: string; type?: string; priority?: string; is_active?: boolean }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.per) queryParams.append('per', params.per.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.priority) queryParams.append('priority', params.priority);
      if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
      
      const url = `/api/announcements/admin${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createAnnouncement(data: { title: string; content: string; type?: string; priority?: string; target_audience?: string; expires_at?: string }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/announcements', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAnnouncement(id: string, data: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/announcements/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAnnouncement(id: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/announcements/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleAnnouncement(id: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/announcements/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Notifications endpoints
  async getNotifications(params?: { limit?: number; unreadOnly?: boolean }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.unreadOnly !== undefined) queryParams.append('unreadOnly', params.unreadOnly.toString());
      
      const url = `/api/notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  async createNotification(data: {
    type: string;
    title: string;
    message: string;
    targetUserId?: string;
    priority?: string;
    expiresAt?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/notifications', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async markAllNotificationsAsRead(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put('/api/notifications/read-all');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getReseller(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/resellers/${id}`);
    return response.data;
  }

  async createReseller(reseller: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/resellers', reseller);
    return response.data;
  }

  async updateReseller(id: string, reseller: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/resellers/${id}`, reseller);
    return response.data;
  }

  async deleteReseller(options: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/resellers/${options.reseller_id}`, { data: options });
    return response.data;
  }

  async getResellerClientsCount(resellerId: string): Promise<{ reseller_id: number; clients: number }> {
    const response: AxiosResponse<any> = await this.api.get(`/api/resellers/${resellerId}/clients-count`);
    return response.data;
  }

  async getResellerActiveClientsTreeCount(resellerId: string): Promise<{
    reseller_id: number;
    active_clients_tree: number;
    active_clients_tree_with_trials: number;
    active_iptv: number;
    active_iptv_with_trials: number;
    active_p2p: number;
    active_p2p_with_trials: number;
  }> {
    const response: AxiosResponse<any> = await this.api.get(`/api/resellers/${resellerId}/active-clients-tree-count`);
    return response.data;
  }

  async migrateResellerClients(resellerId: string, targetMemberId: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/resellers/${resellerId}/migrate-clients`, {
      target_member_id: targetMemberId
    });
    return response.data;
  }

  async getResellerSelectSearch(text: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/resellers/select_search', { search: text });
    return response.data.data;
  }

  async addResellerDomain(resellerId: string, params: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/resellers/${resellerId}/add_domain`, params);
    return response.data;
  }

  async getResellerDomains(resellerId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/resellers/${resellerId}/domains`);
    return response.data.data || response.data;
  }

  async getLazyPlayerDomains(resellerId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/resellers/${resellerId}/domains`);
    return response.data.data || response.data;
  }

  async changeResellerToMaster(resellerId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/resellers/${resellerId}/to_master`);
    return response.data;
  }

  async blockReseller(resellerId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/resellers/${resellerId}/block`);
    return response.data;
  }

  async unblockReseller(resellerId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/resellers/${resellerId}/unblock`);
    return response.data;
  }

  async changeResellerCredits(resellerId: string, value: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/resellers/${resellerId}/credits`, { amount: value });
    return response.data;
  }

  // P2P User endpoints
  async getP2PUsers(filter: UserFilter = {}): Promise<PaginatedResponse<P2PUser>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<P2PUser>>> = await this.api.get('/api/p2p/users', { params: filter });
    return response.data.data;
  }

  async getP2PUser(id: string): Promise<P2PUser> {
    const response: AxiosResponse<ApiResponse<P2PUser>> = await this.api.get(`/api/p2p/users/${id}`);
    return response.data.data;
  }

  async createP2PUser(user: Partial<P2PUser>): Promise<P2PUser> {
    const response: AxiosResponse<ApiResponse<P2PUser>> = await this.api.post('/api/p2p/users', user);
    return response.data.data;
  }

  async updateP2PUser(id: string, user: Partial<P2PUser>): Promise<P2PUser> {
    const response: AxiosResponse<ApiResponse<P2PUser>> = await this.api.put(`/api/p2p/users/${id}`, user);
    return response.data.data;
  }

  async deleteP2PUser(id: string): Promise<void> {
    await this.api.delete(`/api/p2p/users/${id}`);
  }



  async getVODsByCategory(category: string, params: any = {}): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/vods/category/${category}`, { params });
    return response.data;
  }


  async createVOD(vod: Partial<VOD>): Promise<VOD> {
    const response: AxiosResponse<ApiResponse<VOD>> = await this.api.post('/vods', vod);
    return response.data.data;
  }

  async updateVOD(id: string, vod: Partial<VOD>): Promise<VOD> {
    const response: AxiosResponse<ApiResponse<VOD>> = await this.api.put(`/vods/${id}`, vod);
    return response.data.data;
  }

  async deleteVOD(id: string): Promise<void> {
    await this.api.delete(`/vods/${id}`);
  }

  // User Settings endpoints
  async getUserSettings(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/user-settings');
    return response.data;
  }

  async updatePanelSettings(settings: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/user-settings/panel', settings);
    return response.data;
  }

  async updateFeatures(features: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/user-settings/features', features);
    return response.data;
  }

  async addApp(appName: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/user-settings/apps', { appName });
    return response.data;
  }

  async removeApp(appName: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/user-settings/apps/${appName}`);
    return response.data;
  }

  async toggleFavoriteApp(appName: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.patch(`/user-settings/apps/${appName}/favorite`);
    return response.data;
  }

  async updateApiSettings(settings: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/user-settings/api', settings);
    return response.data;
  }

  async updateReportsSettings(settings: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put('/user-settings/reports', settings);
    return response.data;
  }

  async resetUserSettings(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/user-settings/reset');
    return response.data;
  }

  async getSettingsStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/user-settings/stats');
    return response.data;
  }

  // MÃƒÂ©todos para pacotes
  async getPackages(page: number = 1, per: number = 50): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/packages?page=${page}&per=${per}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTrialPackages(page: number = 1, per: number = 50): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/packages/trial?page=${page}&per=${per}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getPackage(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/packages/${id}`);
    return response.data;
  }

  // MÃƒÂ©todos para alterar plano (apenas bouquet)
  async changePlan(lineId: string, packageId: number): Promise<any> {
   
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/lines/${lineId}/change-plan`, {
        package_id: packageId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // MÃƒÂ©todos para clientes online
  async getOnlineClients(params: URLSearchParams): Promise<any> {
    try {
      // Timeout aumentado para 30s devido ÃƒÂ s queries complexas e chamadas P2P
      const response: AxiosResponse<any> = await this.api.get(`/api/lines/online?${params.toString()}`, {
        timeout: 30000 // 30 segundos para queries complexas e chamadas P2P
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // MÃƒÂ©todos para tickets
  async getTickets(params: any = {}): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/tickets', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTicketStats(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/tickets/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createTicket(ticketData: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/tickets', ticketData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // --- Solicitações de conteúdo (filmes/séries) ---
  async searchTmdbContent(query: string, type: 'multi' | 'movie' | 'series' = 'multi'): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/content-requests/tmdb/search', {
      params: { q: query, type },
      timeout: 15000
    });
    return response.data;
  }

  async getTmdbDetails(tmdbId: number, type: 'movie' | 'series'): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/content-requests/tmdb/${tmdbId}`, {
      params: { type },
      timeout: 15000
    });
    return response.data;
  }

  async getTmdbSeason(tmdbId: number, season: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/api/content-requests/tmdb/${tmdbId}/season/${season}`, {
      timeout: 15000
    });
    return response.data;
  }

  async getContentRequests(params: any = {}): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/content-requests', { params });
    return response.data;
  }

  async getContentRequestStats(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/content-requests/stats');
    return response.data;
  }

  async createContentRequest(payload: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/api/content-requests', payload);
    return response.data;
  }

  async voteContentRequest(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/content-requests/${id}/vote`);
    return response.data;
  }

  async watchContentRequest(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/content-requests/${id}/watch`);
    return response.data;
  }

  async commentContentRequest(id: string, body: { body: string; isInternal?: boolean }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/api/content-requests/${id}/comments`, body);
    return response.data;
  }

  async updateContentRequestStatus(id: string, status: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/content-requests/${id}/status`, { status });
    return response.data;
  }

  async updateContentRequestPriority(id: string, priority: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/api/content-requests/${id}/priority`, { priority });
    return response.data;
  }

  async deleteContentRequest(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/api/content-requests/${id}`);
    return response.data;
  }

  async updateTicket(ticketId: string, ticketData: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/tickets/${ticketId}`, ticketData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async addTicketMessage(ticketId: string, messageData: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post(`/api/tickets/${ticketId}/messages`, messageData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTicketStatus(ticketId: string, status: string, reason?: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/tickets/${ticketId}/status`, {
        status,
        reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTicket(ticketId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTicket(ticketId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteTicketsInBulk(ticketIds: string[]): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete('/api/tickets/bulk', {
        data: { ticketIds }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // MÃƒÂ©todo para buscar dados pÃƒÂºblicos do CMS por slug
  // Implementa fallback automÃƒÂ¡tico: tenta API principal, se falhar tenta proxy
  async getCmsPublicData(slug: string): Promise<any> {
    const PROXY_BASE_URL = 'https://xms-proxy.u9ionsrv.workers.dev';
    
    // Tentar primeiro com a API principal
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/cms/${slug}/public`);
      return response.data;
    } catch (error: any) {
      // Verificar se ÃƒÂ© erro de conexÃƒÂ£o/DNS/timeout OU erro 403 (acesso negado)
      // Erro 403 pode indicar bloqueio do domÃƒÂ­nio pelo cliente, entÃƒÂ£o tentar proxy
      const isConnectionError = 
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        error.message?.includes('ECONNREFUSED') ||
        error.message?.includes('ENOTFOUND');
      
      const isForbidden = error.response?.status === 403;
      const isServerError = error.response?.status >= 500 && error.response?.status < 600;
      
      // Tentar proxy se for erro de conexÃƒÂ£o, 403 (acesso negado) ou erro do servidor
      if (isConnectionError || isForbidden || isServerError) {
       
        
        // Tentar com a proxy como fallback
        try {
          const proxyResponse: AxiosResponse<any> = await axios.get(
            `${PROXY_BASE_URL}/api/cms/${slug}/public`,
            {
              timeout: 10000,
              headers: {
                'Accept': 'application/json',
              },
              withCredentials: true,
            }
          );
          
          return proxyResponse.data;
        } catch (proxyError: any) {
          // Se a proxy tambÃƒÂ©m falhar, lanÃƒÂ§ar o erro original
          throw error;
        }
      } else {
        // Se nÃƒÂ£o for erro que justifica usar proxy (ex: 404), lanÃƒÂ§ar erro original
        throw error;
      }
    }
  }

  // VOD Methods
  async getVODs(params?: { page?: number; per?: number; search?: string; type?: string; category?: string }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/vods', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getRecentVODs(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/vods/recent');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getVODCategories(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/vods/categories');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async searchVODs(query: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/vods', { 
        params: { search: query, per: 20 } 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // MÃƒÂ©todo para enviar mensagem em massa
  async sendMassMessage(messageData: { title?: string; content: string; sendOnlyToOnline?: boolean }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/tickets/mass-message', messageData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }






  // Client URLs Methods
  async getClientUrls(clientId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/lines/url/${clientId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async shortenUrl(originalUrl: string, type?: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/lines/shorten', {
        originalUrl,
        type
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async validateDomain(domain: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/lines/domain/validate?domain=${encodeURIComponent(domain)}`);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // Short URLs Methods
  async createShortUrl(data: {
    originalUrl: string;
    urlType: string;
    clientId: string;
    clientUsername: string;
    domain: string;
    expiresAt?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/short-urls', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async generateClientShortUrls(clientId: string, domain: string, expiresAt?: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post(`/api/short-urls/client/${clientId}`, {
        domain,
        expiresAt
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getShortUrlInfo(shortCode: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/short-urls/info/${shortCode}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getLinesShortUrls(params?: {
    page?: number;
    limit?: number;
    clientId?: string;
    urlType?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/short-urls', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getClientShortUrls(clientId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/short-urls/client/${clientId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deactivateShortUrl(shortCode: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/short-urls/${shortCode}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getShortUrlStats(global?: boolean): Promise<any> {
    try {
      const params = global ? { global: 'true' } : {};
      const response: AxiosResponse<any> = await this.api.get('/api/short-urls/stats', { params });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // App Management endpoints (apenas admin)
  async getAppManagement(): Promise<any> {
    try {
      // Buscar todos os apps sem paginaÃƒÂ§ÃƒÂ£o para gerenciamento
      const response: AxiosResponse<any> = await this.api.get('/api/apps', {
        params: {
          page: 1,
          limit: 1000 // Limite alto para buscar todos os apps
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // App Downloads - Buscar aplicativos pÃƒÂºblicos para download no dashboard
  async getAppDownloads(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/apps/public');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async uploadApp(formData: FormData, useDirect: boolean = true): Promise<any> {
    try {
      // SEMPRE usar upload direto para arquivos (bypass Cloudflare Pages)
      // Cloudflare Pages limita em 100MB, API suporta atÃƒÂ© 1GB
      const file = formData.get('file') as File;
      const shouldUseDirect = useDirect || (file && file.size > 10 * 1024 * 1024); // > 10MB
      
      if (shouldUseDirect) {
        // Upload direto usando uploadApi otimizado (sem timeout, sem limites)
        const response: AxiosResponse<any> = await this.uploadApi.post('/api/apps/upload-direct', formData, {
          onUploadProgress: (progressEvent) => {
            // Progresso do upload disponÃƒÂ­vel em progressEvent.loaded / progressEvent.total
            // Callback de progresso poderia ser adicionado aqui no futuro
            if (progressEvent.total) {
              // const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });
        
        return response.data;
      } else {
        // Upload normal via Axios (apenas para arquivos muito pequenos < 10MB)
        const response: AxiosResponse<any> = await this.api.post('/api/apps', formData);
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }

  async updateAppVersion(appId: string, versionId: string, versionData: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}/versions/${versionId}/status`, versionData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // App endpoints especÃƒÂ­ficos
  async getApp(appId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/apps/${appId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateApp(appId: string, appData: any): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}`, appData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAppWithFile(appId: string, formData: FormData, useDirect: boolean = true): Promise<any> {
    try {
      // SEMPRE usar upload direto para arquivos (bypass Cloudflare Pages)
      // Cloudflare Pages limita em 100MB, API suporta atÃƒÂ© 1GB
      const file = formData.get('file') as File;
      const shouldUseDirect = useDirect || (file && file.size > 10 * 1024 * 1024); // > 10MB
      
      if (shouldUseDirect && file) {
        // Upload direto usando uploadApi otimizado (sem timeout, sem limites)
        const response: AxiosResponse<any> = await this.uploadApi.put(`/api/apps/${appId}`, formData, {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Progresso disponÃƒÂ­vel para callback futuro
            }
          }
        });
        return response.data;
      } else {
        // Sem arquivo ou arquivo pequeno - usar API normal
        const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}`, formData);
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteApp(appId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/apps/${appId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleAppStatus(appId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async addAppVersion(appId: string, formData: FormData, useDirect: boolean = true): Promise<any> {
    try {
      // SEMPRE usar upload direto para arquivos (bypass Cloudflare Pages)
      const file = formData.get('file') as File;
      const shouldUseDirect = useDirect || (file && file.size > 10 * 1024 * 1024); // > 10MB
      
      if (shouldUseDirect) {
        // Upload direto usando uploadApi otimizado (sem timeout, sem limites)
        const response: AxiosResponse<any> = await this.uploadApi.post(`/api/apps/${appId}/versions`, formData, {
          onUploadProgress: (progressEvent) => {
            // Progresso do upload disponÃƒÂ­vel em progressEvent.loaded / progressEvent.total
            // Callback de progresso poderia ser adicionado aqui no futuro
            if (progressEvent.total) {
              // const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });
        
        return response.data;
      } else {
        // Upload normal via Axios (passa pelo Cloudflare)
        const response: AxiosResponse<any> = await this.api.post(`/api/apps/${appId}/versions`, formData, {
          // NÃƒÂ£o setar Content-Type para permitir boundary automÃƒÂ¡tico
          timeout: 300000,
          withCredentials: true,
        });
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }

  async toggleVersionStatus(appId: string, versionId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}/versions/${versionId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async downloadApp(appId: string, versionId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/api/apps/${appId}/versions/${versionId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async verifyAppDownloadCode(code: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/apps/verify-code', { code });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAppDownloadCode(appId: string, newCode: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/apps/${appId}/download-code`, { download_code: newCode });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /** Verifica se o usuÃƒÂ¡rio logado ÃƒÂ© o vendedor que recebe os pagamentos deste slug. */
  async getStorePaymentsAccess(): Promise<{
    success: boolean;
    data?: {
      canView: boolean;
      canManage: boolean;
      isOwner: boolean;
      cmsSlug?: string;
      reason?: string;
    };
    error?: string;
  }> {
    const response: AxiosResponse<any> = await this.api.get('/api/store-payments/access');
    return response.data;
  }

  /** Pagamentos automÃƒÂ¡ticos (loja / Mercado Pago) Ã¢â‚¬â€ escopo do painel atual (X-CMS-Slug). */
  async getStorePaymentsSummary(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/store-payments/summary');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getStorePaymentsByDay(params?: { from?: string; to?: string }): Promise<any> {
    try {
      const q = new URLSearchParams();
      if (params?.from) q.append('from', params.from);
      if (params?.to) q.append('to', params.to);
      const url = q.toString() ? `/api/store-payments/by-day?${q.toString()}` : '/api/store-payments/by-day';
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getStorePaymentsList(params?: {
    page?: number;
    per?: number;
    status?: string;
    username?: string;
    from?: string;
    to?: string;
  }): Promise<any> {
    try {
      const q = new URLSearchParams();
      if (params?.page) q.append('page', String(params.page));
      if (params?.per) q.append('per', String(params.per));
      if (params?.status) q.append('status', params.status);
      if (params?.username) q.append('username', params.username);
      if (params?.from) q.append('from', params.from);
      if (params?.to) q.append('to', params.to);
      const url = q.toString() ? `/api/store-payments/list?${q.toString()}` : '/api/store-payments/list';
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /** Remover 1 registro de pagamentos (admin only). */
  async deleteStorePaymentRecord(id: string, kind: 'renewal' | 'credit'): Promise<any> {
    const q = new URLSearchParams({ kind: String(kind || 'renewal') });
    const response: AxiosResponse<any> = await this.api.delete(
      `/api/store-payments/${encodeURIComponent(String(id || '').trim())}?${q.toString()}`
    );
    return response.data;
  }

  /** PrÃƒÂ©-visualizaÃƒÂ§ÃƒÂ£o de limpeza em lote (dono do slug). */
  async getStorePaymentsBulkPreview(params?: {
    status?: string;
    approvedOnly?: boolean;
    kind?: 'renewal' | 'credit' | 'all';
    username?: string;
    from?: string;
    to?: string;
  }): Promise<any> {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.approvedOnly) q.append('approvedOnly', 'true');
    if (params?.kind) q.append('kind', params.kind);
    if (params?.username) q.append('username', params.username);
    if (params?.from) q.append('from', params.from);
    if (params?.to) q.append('to', params.to);
    const response: AxiosResponse<any> = await this.api.get(
      `/api/store-payments/bulk/preview${q.toString() ? `?${q.toString()}` : ''}`
    );
    return response.data;
  }

  /** Limpar registros em lote (dono do slug). */
  async clearStorePaymentRecords(params?: {
    status?: string;
    approvedOnly?: boolean;
    before?: string;
    kind?: 'renewal' | 'credit' | 'all';
    username?: string;
    from?: string;
    to?: string;
  }): Promise<any> {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.approvedOnly) q.append('approvedOnly', 'true');
    if (params?.before) q.append('before', params.before);
    if (params?.kind) q.append('kind', params.kind);
    if (params?.username) q.append('username', params.username);
    if (params?.from) q.append('from', params.from);
    if (params?.to) q.append('to', params.to);
    const response: AxiosResponse<any> = await this.api.delete(
      `/api/store-payments/clear/bulk${q.toString() ? `?${q.toString()}` : ''}`
    );
    return response.data;
  }

  /** Compra de crÃƒÂ©ditos (revendedor) Ã¢â‚¬â€ Mercado Pago / UpdePix do CMS (X-CMS-Slug). */
  async getCreditPurchaseCatalog(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/api/credit-purchases/catalog');
    return response.data;
  }

  async createCreditPurchaseMercadoPago(body: {
    packageId: string;
    buyerMemberId?: number;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      '/api/credit-purchases/mercadopago',
      body
    );
    return response.data;
  }

  async createCreditPurchaseUpdePix(body: {
    packageId: string;
    buyerMemberId?: number;
    payerDocument?: string;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      '/api/credit-purchases/updepix',
      body
    );
    return response.data;
  }

  async createCreditPurchaseLynx(body: {
    packageId: string;
    buyerMemberId?: number;
    payerDocument?: string;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(
      '/api/credit-purchases/lynx',
      body
    );
    return response.data;
  }

  /** Estado do pedido de compra de crÃƒÂ©ditos (PIX / webhook). */
  async getCreditPurchaseOrderStatus(orderId: string): Promise<any> {
    const q = new URLSearchParams({ orderId: String(orderId || '').trim() });
    const response: AxiosResponse<any> = await this.api.get(
      `/api/credit-purchases/order-status?${q.toString()}`
    );
    return response.data;
  }

  // Short URLs Management
  async getShortUrls(params?: { page?: number; limit?: number; urlType?: string }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.urlType) queryParams.append('urlType', params.urlType);
      
      const url = queryParams.toString() ? `/api/short-urls?${queryParams.toString()}` : '/api/short-urls';
      const response: AxiosResponse<any> = await this.api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleShortUrl(id: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/short-urls/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async regenerateAppShortUrl(appId: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post(`/api/apps/${appId}/regenerate-link`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateShortUrl(id: string, data: { originalUrl?: string; urlType?: string; clientUsername?: string }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/short-urls/${id}`, data);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async deleteShortUrl(id: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/short-urls/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async bulkDeleteShortUrls(ids: string[]): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/short-urls/bulk-delete', { ids });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async bulkDomainChange(oldDomain: string, newDomain: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/short-urls/bulk-domain-change', {
        oldDomain,
        newDomain
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ===========================================
  // NOTIFICAÃƒâ€¡Ãƒâ€¢ES ADMIN METHODS
  // ===========================================

  async getNotificationsAdmin(params: {
    page?: number;
    per?: number;
    search?: string;
    type?: string;
    priority?: string;
    isRead?: boolean;
    userId?: string;
  } = {}): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.per) queryParams.append('per', params.per.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.type) queryParams.append('type', params.type);
      if (params.priority) queryParams.append('priority', params.priority);
      if (params.isRead !== undefined) queryParams.append('isRead', params.isRead.toString());
      if (params.userId) queryParams.append('userId', params.userId);

      const response: AxiosResponse<any> = await this.api.get(`/api/notifications/admin?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createNotificationAdmin(data: {
    type: string;
    title: string;
    message: string;
    targetUserId?: string;
    priority?: string;
    expiresAt?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/notifications/admin', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateNotificationAdmin(id: string, data: {
    type?: string;
    title?: string;
    message?: string;
    priority?: string;
    expiresAt?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.put(`/api/notifications/admin/${id}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteNotificationAdmin(id: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete(`/api/notifications/admin/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteNotificationsBulk(data: {
    notificationIds?: string[];
    filters?: {
      type?: string;
      priority?: string;
      isRead?: boolean;
      userId?: string;
      createdBefore?: string;
      expired?: boolean;
    };
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.delete('/api/notifications/admin/bulk', {
        data
      });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // ===========================================
  // SESSÃƒâ€¢ES E AUTENTICAÃƒâ€¡ÃƒÆ’O ADMIN METHODS
  // ===========================================

  async clearAllUserSessions(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/api/auth/clear-all-sessions');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getActiveSessions(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/api/auth/active-sessions');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== SYSTEM SETTINGS - LIMPEZA DE LINHAS ====================

  async getLineCleanupSettings(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/system-settings/line-cleanup');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async updateLineCleanupSettings(settings: { enabled: boolean; cron_expression?: string }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put('/api/system-settings/line-cleanup', settings);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async executeManualLineCleanup(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/system-settings/line-cleanup/manual');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async getLineCleanupStats(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/system-settings/line-cleanup/stats');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // ==================== PLATAFORMA EDGE/P2P (ADMIN) ====================

  async getStreamingPlatformSettings(): Promise<any> {
    const response: AxiosResponse = await this.api.get('/api/system-settings/streaming-platform');
    return response.data;
  }

  async updateStreamingPlatformSettings(payload: {
    defaultEdgeApiUrl?: string;
    edgeApiUrlFallbacks?: string;
    edgeDomains?: string;
    defaultP2pBrokerUrl?: string;
    p2pBrokerUrlFallbacks?: string;
    p2pEnabledDefault?: boolean;
    adminNotes?: string;
  }): Promise<any> {
    const response: AxiosResponse = await this.api.put('/api/system-settings/streaming-platform', payload);
    return response.data;
  }

  async flushStreamingPlatformCaches(): Promise<any> {
    const response: AxiosResponse = await this.api.post('/api/system-settings/streaming-platform/flush-caches');
    return response.data;
  }

  // ==================== TRIAL GENERATOR ====================

  async getTrialGeneratorSettings(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/trial-generator/settings');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async updateTrialGeneratorSettings(settings: { xuiApiUrl?: string; xuiApiToken?: string; xuiTrialPackageId?: number; appDownloadUrl?: string; trialCooldownHours?: number; trialGeneratorEnabled?: boolean; xuiIndependentEnabled?: boolean }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put('/api/trial-generator/settings', settings);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async validateXuiToken(xuiApiUrl: string, xuiApiToken: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/validate-token', {
        xuiApiUrl,
        xuiApiToken
      });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async generateTrial(
    packageId?: number,
    duration?: number,
    maxConnections?: number,
    options?: { quickTest?: boolean }
  ): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/create', {
        packageId,
        duration,
        maxConnections,
        quickTest: options?.quickTest === true
      });
      return response.data;
      } catch (error) {
            throw error;
    }
  }

  /**
   * Prepara dados para chamar a XUI diretamente do front (teste rÃƒÂ¡pido).
   * Retorna postUrl para o front fazer POST do browser para a XUI-One.
   */
  async prepareTrialDirect(
    packageId?: number,
    duration: number = 6,
    maxConnections: number = 1
  ): Promise<{ success: boolean; data?: { username: string; password: string; postUrl: string; expDate: string; duration: number }; error?: string; needsSetup?: boolean }> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/prepare-direct', {
        packageId,
        duration,
        maxConnections
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao preparar trial',
        needsSetup: error.response?.data?.needsSetup
      };
    }
  }

  async regeneratePublicTrialToken(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/regenerate-public-token');
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // ==================== GERENCIAMENTO DE DEVICES (MAC/Android ID) ====================

  async getTrials(
    page: number = 1,
    limit: number = 20,
    search?: string,
    platform: 'all' | 'android' | 'smarttv' = 'all'
  ): Promise<any> {
    try {
      let url = `/api/trial-generator/trials?page=${page}&limit=${limit}`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (platform && platform !== 'all') {
        url += `&platform=${encodeURIComponent(platform)}`;
      }
      const response: AxiosResponse = await this.api.get(url);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async updateDeviceUser(deviceId: string, newUsername?: string, newPassword?: string, useProxy?: boolean | null): Promise<any> {
    try {
      const payload: any = {};
      if (newUsername !== undefined && newUsername !== null && newUsername.trim() !== '') {
        payload.newUsername = newUsername.trim();
      }
      if (newPassword !== undefined) {
        // Enviar senha mesmo se for string vazia (para limpar)
        payload.newPassword = newPassword !== null ? newPassword : '';
      }
      if (useProxy !== undefined) {
        // Enviar null para "automÃƒÂ¡tico" (seguir configuraÃƒÂ§ÃƒÂ£o global)
        payload.useProxy = useProxy;
      }
      
            const response: AxiosResponse = await this.api.put(`/api/trial-generator/trials/device/${deviceId}/update-user`, payload);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async registerDevice(deviceId: string, username: string, cmsSlug: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/register-device', {
        deviceId,
        username,
        cmsSlug
      });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async checkDevice(deviceId: string, cmsSlug?: string): Promise<any> {
    try {
      const url = cmsSlug 
        ? `/api/trial-generator/device/${deviceId}/check?cmsSlug=${cmsSlug}`
        : `/api/trial-generator/device/${deviceId}/check`;
      const response: AxiosResponse = await this.api.get(url);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async activateSmartTv(deviceId: string): Promise<any> {
    const encodedDeviceId = encodeURIComponent(deviceId);
    const response: AxiosResponse = await this.api.post(`/api/trial-generator/trials/device/${encodedDeviceId}/smart-tv/activate`);
    return response.data;
  }

  async deleteDevice(deviceId: string): Promise<any> {
    try {
      // Codificar o deviceId para garantir que caracteres especiais como ":" sejam tratados corretamente na URL
      const encodedDeviceId = encodeURIComponent(deviceId);
      const response: AxiosResponse = await this.api.delete(`/api/trial-generator/trials/device/${encodedDeviceId}`);
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async getBulkDeletePreview(filterType: 'inactive' | 'expired' | 'both' | 'all'): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get(
        `/api/trial-generator/trials/bulk-delete/preview?filterType=${filterType}`,
        { timeout: 300000 }
      );
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  async bulkDeleteDevices(filterType: 'inactive' | 'expired' | 'both' | 'all'): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/trial-generator/trials/bulk-delete', {
        filterType,
        confirm: true
      }, { timeout: 600000 });
      return response.data;
    } catch (error) {
            throw error;
    }
  }

  // ==================== WHATSAPP AUTOMATION ====================

  async getWhatsappAutomationStatus(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async startWhatsappAutomation(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/start');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async logoutWhatsappAutomation(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/logout');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async resetWhatsappAutomation(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/reset');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendWhatsappAutomationMessage(payload: { phone: string; message: string; templateId?: string; triggerId?: string; variables?: Record<string, any> }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/send-message', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWhatsappAutomationTemplates(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/templates');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveWhatsappTemplate(template: { name: string; body: string; category?: string; placeholders?: any[] }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/templates', template);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteWhatsappTemplate(templateId: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.delete(`/api/whatsapp-automation/templates/${templateId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obter template padrÃƒÂ£o de aviso de vencimento
  async getExpirationWarningTemplate(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/templates/expiration-warning');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Atualizar template padrÃƒÂ£o de aviso de vencimento
  async updateExpirationWarningTemplate(body: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put('/api/whatsapp-automation/templates/expiration-warning', { body });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obter template padrÃƒÂ£o de renovaÃƒÂ§ÃƒÂ£o (para copiar no painel e disparos)
  async getRenewalTemplate(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/templates/renewal');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Atualizar template padrÃƒÂ£o de renovaÃƒÂ§ÃƒÂ£o
  async updateRenewalTemplate(body: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put('/api/whatsapp-automation/templates/renewal', { body });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Disparo AutomÃƒÂ¡tico DiÃƒÂ¡rio
  async getWhatsappDailyDispatchConfig(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/daily-dispatch/config');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateWhatsappDailyDispatchConfig(config: { enabled: boolean; templateId?: string; hour?: number; daysBeforeExpiration?: number }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put('/api/whatsapp-automation/daily-dispatch/config', config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async testWhatsappDailyDispatch(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/daily-dispatch/test');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveWhatsappAutomationTemplate(template: { name: string; body: string; category?: string; placeholders?: Array<{ key: string; description?: string }> }): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/templates', template);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWhatsappAutomationLogs(params: { page?: number; limit?: number; status?: string } = {}): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/logs', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWhatsappTriggers(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/triggers');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveWhatsappTrigger(triggerData: any): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/triggers', triggerData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleWhatsappTrigger(triggerId: string, isActive: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.put(`/api/whatsapp-automation/triggers/${triggerId}/toggle`, { isActive });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteWhatsappTrigger(triggerId: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.delete(`/api/whatsapp-automation/triggers/${triggerId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async testWhatsappTrigger(triggerId: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post(`/api/whatsapp-automation/triggers/${triggerId}/test`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWhatsappQueueStatus(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/whatsapp-automation/queue/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getExpiringClientsWithWhatsapp(params: { 
    days?: number; 
    page?: number; 
    limit?: number; 
    onlyWithWhatsapp?: boolean;
  } = {}): Promise<any> {
    try {
      const { days = 7, page = 1, limit = 50, onlyWithWhatsapp = true } = params;
      const response: AxiosResponse = await this.api.get('/api/dashboard/expiring-clients-whatsapp', {
        params: {
          days,
          page,
          limit,
          onlyWithWhatsapp: onlyWithWhatsapp ? 'true' : 'false'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async clearWhatsappQueue(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/queue/clear');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendBulkWhatsapp(clientIds: number[], templateId: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/whatsapp-automation/send-bulk', {
        clientIds,
        templateId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== MÃƒâ€°TODOS DE SEGURANÃƒâ€¡A (APENAS ADMIN) ====================
  
  /**
   * GET /api/security/blacklist
   * Lista IPs bloqueados
   */
  async getBlacklist(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/security/blacklist');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * POST /api/security/blacklist
   * Adiciona IP ÃƒÂ  blacklist permanente
   */
  async addToBlacklist(ip: string, reason?: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/security/blacklist', {
        ip,
        reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * DELETE /api/security/blacklist/:ip
   * Remove IP da blacklist
   */
  async removeFromBlacklist(ip: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.delete(
        `/api/security/blacklist/${encodeURIComponent(ip)}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * POST /api/security/blacklist/temp-block
   * Bloqueia IP temporariamente
   */
  async tempBlockIP(ip: string, minutes: number, reason?: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.post('/api/security/blacklist/temp-block', {
        ip,
        minutes,
        reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * GET /api/security/stats
   * EstatÃƒÂ­sticas de seguranÃƒÂ§a
   */
  async getSecurityStats(): Promise<any> {
    try {
      const response: AxiosResponse = await this.api.get('/api/security/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

}

export const apiService = new ApiService();
